import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Droppable } from '@hello-pangea/dnd';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import FaIconUvilo from '@erikdakoda/ui/components/FaIconUvilo';
import { Poof } from '@erikdakoda/ui/components/Poof';
import { useLabelDragStore } from '../hooks/useLabelDragStore';
import { removeZoneDroppableId } from '../shared/labelDragIds';
import { LabelRemoveZoneTracker } from './LabelDragTrackers';

const ZONE_HEIGHT_PX = 52;

type LabelRemoveZoneProps = {
  /** Dnd type of this sidebar's item drags. */
  droppableType: string;
  /** Droppable-id suffix; defaults to `droppableType`. */
  dragScope?: string;
};

type SidebarBounds = {
  top: number;
  left: number;
  width: number;
  height: number;
};

/**
 * Resolves the sidebar column to size the desktop remove strip against — drawer paper when present,
 * otherwise the outer `*-sidebar` shell (not `.label-sidebar`, which is the scrollable list root).
 */
function findSidebarColumn(from: HTMLElement): HTMLElement {
  const drawerPaper = from.closest('.MuiDrawer-paper');
  if (drawerPaper instanceof HTMLElement) {
    return drawerPaper;
  }

  let node: HTMLElement | null = from.parentElement;
  while (node) {
    const className = typeof node.className === 'string' ? node.className : '';
    const tokens = className.split(/\s+/);
    const shellClass = tokens.find(
      (token) =>
        token.endsWith('-sidebar') &&
        token !== 'label-sidebar' &&
        !token.startsWith('label-remove-zone'),
    );
    if (shellClass) {
      return node;
    }
    node = node.parentElement;
  }

  return from;
}

/**
 * Reads the desktop remove strip's viewport box from a sidebar column element.
 * The strip is half the sidebar width, inset from the sidebar by a quarter of the sidebar width
 * on the content side.
 */
function readDesktopBounds(column: HTMLElement): SidebarBounds | null {
  const rect = column.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) {
    return null;
  }
  const width = rect.width / 2;
  const gap = rect.width / 4;
  // Prefer the content side of the sidebar so right drawers still drop toward the main pane.
  const placeOnRight = rect.left + rect.width / 2 < window.innerWidth / 2;
  return {
    top: rect.top,
    left: placeOnRight ? rect.right + gap : rect.left - gap - width,
    width,
    height: rect.height,
  };
}

/**
 * Drop target that removes an item from its folder — `circle-minus`, never a trash can, because the
 * item itself is not deleted.
 *
 * Desktop: an invisible strip on the content-side of the sidebar, half the sidebar width and
 * separated from the sidebar by a quarter of the sidebar width, portaled so scroll/overflow
 * clipping cannot shrink the hit area. The strip stays invisible; a `Poof` plays at the release
 * pointer. Mobile: a sticky bottom strip that fades in while a drag from a main or secondary
 * section is active; the same release-point poof is used.
 *
 * The droppable stays mounted with real dimensions at all times: `@hello-pangea/dnd` measures
 * droppable boxes when the drag starts.
 */
export default function LabelRemoveZone({ droppableType, dragScope }: LabelRemoveZoneProps) {
  'use memo';

  const { t } = useTranslation('label-ui');
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });
  const isDragging = useLabelDragStore((state) => state.isDragging);
  const sourceKind = useLabelDragStore((state) => state.sourceKind);
  const poofBurst = useLabelDragStore((state) => state.poofBurst);
  const poofX = useLabelDragStore((state) => state.poofX);
  const poofY = useLabelDragStore((state) => state.poofY);
  const poofWidth = useLabelDragStore((state) => state.poofWidth);
  const poofHeight = useLabelDragStore((state) => state.poofHeight);
  const [isPoofing, setIsPoofing] = useState(false);
  const [bounds, setBounds] = useState<SidebarBounds | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (poofBurst === 0) {
      return;
    }
    setIsPoofing(true);
  }, [poofBurst]);

  useLayoutEffect(() => {
    if (!isDesktop) {
      setBounds(null);
      return undefined;
    }

    const updateBounds = (): void => {
      const anchor = anchorRef.current;
      if (!anchor) {
        return;
      }
      const next = readDesktopBounds(findSidebarColumn(anchor));
      if (next) {
        setBounds(next);
      }
    };

    updateBounds();
    window.addEventListener('resize', updateBounds);
    window.addEventListener('scroll', updateBounds, true);
    const anchor = anchorRef.current;
    const column = anchor ? findSidebarColumn(anchor) : null;
    const observer =
      typeof ResizeObserver !== 'undefined' && column
        ? new ResizeObserver(() => {
            updateBounds();
          })
        : null;
    if (column && observer) {
      observer.observe(column);
    }
    return () => {
      window.removeEventListener('resize', updateBounds);
      window.removeEventListener('scroll', updateBounds, true);
      observer?.disconnect();
    };
  }, [isDesktop]);

  const canRemove = isDragging && (sourceKind === 'main' || sourceKind === 'secondary');
  const active = canRemove || isPoofing;
  const droppableId = removeZoneDroppableId(dragScope ?? droppableType);

  const handlePoofComplete = (): void => {
    setIsPoofing(false);
  };

  const releasePoof =
    isPoofing && typeof document !== 'undefined'
      ? createPortal(
          <Box
            className="label-remove-zone-poof-host"
            sx={{
              position: 'fixed',
              left: poofX,
              top: poofY,
              width: poofWidth,
              height: poofHeight,
              transform: 'translate(-50%, -50%)',
              zIndex: theme.zIndex.modal + 2,
              pointerEvents: 'none',
            }}
            aria-hidden
          >
            <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
              <Poof
                burst={poofBurst}
                width={poofWidth}
                height={poofHeight}
                onComplete={handlePoofComplete}
              />
            </Box>
          </Box>,
          document.body,
        )
      : null;

  const desktopDroppable =
    bounds === null ? null : (
      <Droppable droppableId={droppableId} type={droppableType} isDropDisabled={!canRemove}>
        {(provided, snapshot) => (
          <Box
            className="label-remove-zone label-remove-zone--desktop"
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{
              position: 'fixed',
              top: bounds.top,
              left: bounds.left,
              width: bounds.width,
              height: bounds.height,
              zIndex: theme.zIndex.drawer + 2,
              opacity: 0,
              pointerEvents: canRemove ? 'auto' : 'none',
            }}
            aria-hidden
          >
            <LabelRemoveZoneTracker isOver={snapshot.isDraggingOver} />
            <Box sx={{ display: 'none' }}>{provided.placeholder}</Box>
          </Box>
        )}
      </Droppable>
    );

  const mobileDroppable = (
    <Droppable droppableId={droppableId} type={droppableType} isDropDisabled={!canRemove}>
      {(provided, snapshot) => (
        <Box
          className="label-remove-zone label-remove-zone--mobile"
          ref={provided.innerRef}
          {...provided.droppableProps}
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            height: ZONE_HEIGHT_PX,
            mx: 2,
            borderRadius: 1,
            border: '1px dashed',
            borderColor: snapshot.isDraggingOver ? 'error.main' : 'divider',
            backgroundColor: 'background.paper',
            color: snapshot.isDraggingOver ? 'error.main' : 'text.secondary',
            opacity: active ? 1 : 0,
            pointerEvents: active ? undefined : 'none',
            transition: 'opacity 150ms ease-out',
          }}
        >
          <LabelRemoveZoneTracker isOver={snapshot.isDraggingOver} />
          <FaIconUvilo icon="circle-minus" />
          <Typography variant="caption">{t('dropToRemove')}</Typography>
          <Box sx={{ display: 'none' }}>{provided.placeholder}</Box>
        </Box>
      )}
    </Droppable>
  );

  return (
    <>
      <Box ref={anchorRef} className="label-remove-zone" aria-hidden sx={{ display: 'none' }} />
      {isDesktop
        ? bounds !== null && typeof document !== 'undefined'
          ? createPortal(desktopDroppable, document.body)
          : null
        : mobileDroppable}
      {releasePoof}
    </>
  );
}
